import { Phone, Award, Home, Heart } from "lucide-react";
import { motion } from "framer-motion";
import { contactInfo } from "../data";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function About() {
  return (
    <div className="bg-white overflow-hidden">
      {/* Header Section */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 flex items-center justify-center text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/treatment-detail.png"
            alt="Om Pernille baggrund"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"></div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8"
        >
          <h1 className="text-4xl font-light tracking-tight text-white sm:text-6xl">Om Pernille</h1>
          <p className="mt-6 text-lg text-stone-200 max-w-xl mx-auto">
            Lær mennesket bag Juhler Fodpleje at kende. Faglig stolthed og tryghed i centrum.
          </p>
        </motion.div>
      </section>

      {/* Intro Section */}
      <section className="relative py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <span className="text-orange-900 font-semibold uppercase tracking-widest text-sm mb-4 block">Mød din fodplejer</span>
              <h2 className="text-3xl tracking-tight text-stone-900 sm:text-4xl mb-8">
                Pernille Juhler
              </h2>
              <div className="space-y-6 text-lg text-stone-600 leading-relaxed">
                <p>
                  Jeg hedder Pernille, og jeg står bag Juhler Fodpleje. Som lægeexamineret fodplejer brænder jeg for at gøre en forskel for dine fødder og dit generelle velvære.
                </p>
                <p>
                  Fødderne er vores fundament, men vi glemmer dem ofte i en travl hverdag. Hvad enten du foretrækker at komme til en afslappende behandling i klinikken eller ønsker et udekørende hjemmebesøg i vante rammer, står jeg klar til at hjælpe dig. Jeg tilbyder behandling både i mine faste klinikker og som udekørende fodplejer.
                </p>
                <p>
                  For mig er fodpleje ikke bare et arbejde – det er et håndværk, hvor tryghed, ordentlighed og menneskelig kontakt er i centrum. Jeg tager mig altid god tid til hver enkelt behandling, uanset om vi mødes i klinikken eller hjemme hos dig.
                </p>
              </div>
              
              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="flex gap-4">
                  <div className="h-10 w-10 shrink-0 rounded-full bg-orange-100 flex items-center justify-center border border-orange-200">
                    <Award className="h-5 w-5 text-stone-700" />
                  </div>
                  <div>
                    <h3 className="text-stone-900 font-medium">Lægeexamineret fodplejer</h3>
                    <p className="text-sm text-stone-500">Professionel baggrund og høj faglighed.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="h-10 w-10 shrink-0 rounded-full bg-orange-100 flex items-center justify-center border border-orange-200">
                    <Home className="h-5 w-5 text-stone-700" />
                  </div>
                  <div>
                    <h3 className="text-stone-900">Klinik & Udekørende</h3>
                    <p className="text-sm text-stone-500">I klinikken eller hjemme hos dig i vante rammer.</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-2"
            >
              <div className="relative">
                <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl relative z-10">
                   <img src="https://assets.auxodev.dk/assets/aec437a7-dbd3-431a-ae4e-b42ebba93fa1/69589887-0f7f-4aab-9013-7a11d7617662.png" alt="Pernille i gang med behandling" className="h-full w-full object-cover" />
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-orange-50 rounded-full -z-0"></div>
                <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-stone-100 rounded-3xl -z-0 rotate-12"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 bg-stone-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            {...fadeIn}
            className="max-w-3xl mx-auto text-center"
          >
            <Heart className="h-12 w-12 text-orange-400 mx-auto mb-8" />
            <h2 className="text-3xl text-stone-900 mb-6">Min filosofi</h2>
            <p className="text-xl text-stone-600 leading-relaxed italic">
              "Det skal være en behagelig og rolig oplevelse at få plejet sine fødder. Hos mig er du ikke bare et nummer i rækken – jeg vægter samtalen og det personlige møde lige så højt som selve fodbehandlingen."
            </p>
            <div className="mt-10 h-px w-24 bg-orange-200 mx-auto"></div>
            <p className="mt-10 font-bold font-serif text-2xl text-stone-900">Pernille Juhler</p>
            <p className="text-stone-500 text-sm uppercase tracking-widest mt-1">Ejer af Juhler Fodpleje</p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
           <motion.div 
              {...fadeIn}
              className="bg-brand-dark rounded-[2.5rem] p-8 md:p-16 text-center text-white relative overflow-hidden border border-brand-dark/20"
            >
              <div className="relative z-10">
                <h2 className="text-3xl mb-6">Skal vi finde en tid?</h2>
                <p className="text-stone-300 text-lg mb-10 max-w-xl mx-auto">
                  Jeg tilbyder behandling både i mine klinikker og som udekørende i Sydjylland og på Sydfyn. Kontakt mig for en uforpligtende snak om dine behov.
                </p>
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="inline-flex items-center gap-4 rounded-full bg-orange-100 px-10 py-5 text-xl font-black text-stone-900 shadow-xl hover:bg-orange-200 transition-all active:scale-95 border border-orange-200"
                >
                  <Phone className="h-7 w-7" />
                  Ring og book: {contactInfo.phoneDisplay}
                </a>
              </div>
           </motion.div>
        </div>
      </section>
    </div>
  );
}
